// src/hooks/trainings/useCreateTraining.ts
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
	TrainingsProxy,
	scheduleTrainingListsInvalidation,
} from '@entities/training'
import { toast } from 'sonner'
import { ExerciseParams } from '@pages/TrainingConstructor/types'
import { buildWorkoutExerciseRequest } from '@entities/training/lib/helpers/buildWorkoutExerciseRequest'
import { CreateTrainingResponse } from '@entities/training/types/create'
import { Training } from '@entities/training/types/base'

interface CreateTrainingParams {
	title: string
	description: string
	exerciseParams: ExerciseParams[]
}

function findCreatedTraining(
	trainings: Training[],
	params: CreateTrainingParams
): Training | undefined {
	return trainings
		.filter(
			(training) =>
				training.name === params.title &&
				(training.description ?? '') === params.description
		)
		.sort(
			(firstTraining, secondTraining) =>
				new Date(secondTraining.created_at).getTime() -
				new Date(firstTraining.created_at).getTime()
		)[0]
}

async function createTrainingRequest(data: CreateTrainingParams): Promise<CreateTrainingResponse> {
	const createdTraining = await TrainingsProxy.createTraining({
		name: data.title,
		description: data.description,
		exercises: data.exerciseParams.map(buildWorkoutExerciseRequest),
	})

	if (createdTraining?.id_workout) {
		return createdTraining
	}

	const trainings = await TrainingsProxy.getTrainings({ start: 0, limit: 50 })
	const fallbackTraining = findCreatedTraining(trainings, data)

	if (!fallbackTraining) {
		throw new Error('Тренировка создана, но не удалось открыть её автоматически')
	}

	return fallbackTraining
}

export const useCreateTraining = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: createTrainingRequest,
		onSuccess: () => {
			scheduleTrainingListsInvalidation(queryClient)

			toast.success('Тренировка успешно создана', {
				position: 'top-right',
				duration: 3000,
			})
		},
		onError: (error: Error) => {
			const errorMessage = error.message.includes('Произошла ошибка')
				? error.message
				: 'Произошла ошибка во время создания тренировки'

			toast.error(errorMessage, {
				position: 'top-right',
				duration: 3000,
				className: 'text-red-300',
			})
		},
	})
}
